// Pure derivation of a household's door-state from its contacts (server snapshot
// merged with not-yet-synced local writes). Used by the list, map, and door screen.

import type { CandidateTags } from '../db/types';

export interface ContactLike {
  support_level: number | null;
  disposition: string | null;
  candidate_tags: CandidateTags;
  issue_tags: string[];
  created_at: string;
  client_uuid?: string | null;
}

export interface HouseholdState {
  supportLevel: number | null; // most recent ASCERTAINED support (1-5), else null
  visited: boolean;
  attempts: number;
  lastAttemptAt: string | null;
  lastDisposition: string | null;
  candidateLeans: string[]; // nation_slugs that resonated, across all contacts
}

function slicesOf(tags: CandidateTags): string[] {
  if (Array.isArray(tags)) return [];
  return Object.keys(tags ?? {});
}

/** contacts: ALL contacts for one household, any order. */
export function deriveState(contacts: ContactLike[]): HouseholdState {
  if (!contacts.length) {
    return { supportLevel: null, visited: false, attempts: 0, lastAttemptAt: null, lastDisposition: null, candidateLeans: [] };
  }
  const sorted = [...contacts].sort((a, b) => (a.created_at < b.created_at ? 1 : -1)); // newest first
  const support = sorted.find((c) => c.support_level != null)?.support_level ?? null;
  const leans = new Set<string>();
  for (const c of contacts) for (const s of slicesOf(c.candidate_tags)) leans.add(s);
  return {
    supportLevel: support,
    visited: true,
    attempts: contacts.length,
    lastAttemptAt: sorted[0]!.created_at,
    lastDisposition: sorted[0]!.disposition ?? null,
    candidateLeans: [...leans],
  };
}

/** A short status line for list rows, e.g. "leaning Karen · not home ×2". */
export function statusLine(s: HouseholdState, slugToName: (slug: string) => string): string {
  if (!s.visited) return 'Not yet visited';
  const parts: string[] = [];
  if (s.candidateLeans.length) parts.push('leaning ' + s.candidateLeans.map(slugToName).join(', '));
  if (s.lastDisposition && s.supportLevel == null) {
    const label = s.lastDisposition.replace(/_/g, ' ');
    parts.push(s.attempts > 1 ? `${label} ×${s.attempts}` : label);
  }
  if (!parts.length && s.supportLevel != null) parts.push('recorded');
  return parts.join(' · ') || 'recorded';
}
